import animateIn from './animateIn.js';
import swiper from './swiper.js';
import { retryHtmxCsrfFailure } from './csrfRequest.js';

const paginationScrollTargets = new WeakSet();
const csrfRetriedForms = new WeakSet();
const reloadOnErrorEvents = [
    'htmx:responseError',
    'htmx:sendError',
    'htmx:timeout'
];

function reloadMarkedTargetOnError(event) {
    const target = event.detail?.target;

    if (target?.hasAttribute('data-reload-on-htmx-error')) {
        window.location.reload();
    }
}

const htmxEvents = {
    initAlpineTree(target, initializedTargets = new Set()) {
        if (
            !target ||
            !window.Alpine ||
            typeof window.Alpine.initTree !== 'function'
        ) {
            return;
        }

        const initTarget = (element) => {
            if (
                !(element instanceof Element) ||
                initializedTargets.has(element)
            ) {
                return;
            }

            initializedTargets.add(element);
            window.Alpine.initTree(element);
        };

        initTarget(target);

        if (!target.classList?.contains('sprig-component')) {
            return;
        }

        const sibling = target.nextElementSibling;
        initTarget(sibling);
    },

    init() {
        if (window.htmx) {
            reloadOnErrorEvents.forEach((eventName) => {
                window.htmx.on(eventName, reloadMarkedTargetOnError);
            });

            window.htmx.on('htmx:beforeRequest', function (event) {
                const target = event.detail.target;
                target.classList.add('is-loading');

                const trigger = event.detail.elt;
                if (trigger?.closest?.('[data-sprig-pagination-nav]')) {
                    paginationScrollTargets.add(target);
                } else {
                    paginationScrollTargets.delete(target);
                }
            });

            window.htmx.on('htmx:responseError', function (event) {
                retryHtmxCsrfFailure(event, csrfRetriedForms).catch(() => {});
            });

            window.htmx.on('htmx:afterRequest', function (event) {
                event.detail.target.classList.remove('is-loading');
            });

            window.htmx.on('htmx:afterSwap', function (event) {
                const target = event.detail.target;

                if (target) {
                    const initializedAlpineTargets = new Set();
                    htmxEvents.initAlpineTree(target, initializedAlpineTargets);

                    // Small delay to ensure DOM is fully updated
                    requestAnimationFrame(() => {
                        htmxEvents.initAlpineTree(
                            target,
                            initializedAlpineTargets
                        );
                        animateIn.refresh(target);
                        // Initialize any new Swiper instances in the swapped content
                        swiper.refresh(target);
                    });
                }

                const scrollTop = target.hasAttribute('data-sprig-scroll-top')
                    ? target
                    : target.querySelector('[data-sprig-scroll-top]');

                if (scrollTop) {
                    const scrollMode = scrollTop.getAttribute(
                        'data-sprig-scroll-top'
                    );
                    const shouldScroll =
                        scrollMode !== 'pagination-only' ||
                        paginationScrollTargets.has(target);

                    if (!shouldScroll) {
                        paginationScrollTargets.delete(target);
                        return;
                    }

                    requestAnimationFrame(() => {
                        const rect = scrollTop.getBoundingClientRect();
                        const offset = rect.top + window.scrollY - 300;
                        window.scrollTo({ top: offset, behavior: 'smooth' });
                    });
                }

                paginationScrollTargets.delete(target);
            });
        }
    }
};

export default htmxEvents;
